// Single source of truth for "when does this game read as" in the league,
// coach, and official consoles (KAN-49, 2026-09-09).
//
// THE CONTRACT
//
// A game is stored as the VENUE's wall clock (`gameDate` "YYYY-MM-DD" +
// `gameTime` "HH:mm") plus an IANA `timeZone` naming the zone that pair
// belongs to. `startsAtUtc` is the same moment as an absolute instant,
// published additively by polyp-league / polyp-coach / polyp-official-games so
// a browser doesn't have to reimplement tzOffsetMs to build one.
//
// Reading converts that instant into the VIEWER's zone and appends the
// viewer's zone label. So a 2:05 PM game scheduled in Miami reads
// "2:05 PM EDT" in Miami and "12:05 PM MDT" in Denver — same instant, each
// reader's clock, and the label says whose.
//
// This deliberately mirrors ios/Polyp/Entities/PolypGameTime.swift (the
// `display` half) and the per-page `localWhen` in playerwiki/web/team.html and
// league.html. Those are copy-deliberately siblings — separate repos and
// separate runtimes, kept in agreement by hand. Before this file the three
// console pages each carried their own `prettyDate`/`prettyTime` pair and
// rendered a bare "2:05 PM" with no zone at all, which is what let a league
// admin in Denver schedule a Miami game with nothing telling them whose 2:05
// it was.
//
// WHAT IS *NOT* CONVERTED
//
// An instant needs a date AND a time. A game carrying only a date has no
// instant, so its date is rendered verbatim — converting "midnight at the
// venue" into a western zone would move it to the previous day and invent an
// error that was never in the data. Same reasoning keeps the no-instant
// helpers below pinned to UTC.

// "2026-09-13" -> "Sat, Sep 13". Pure string reformat: the input is a bare
// calendar date with no zone, so parsing and formatting both pin UTC and
// cancel out — the date in is the date out, on any machine. Unparseable input
// is returned unchanged rather than blanked.
export function venueDateText(iso, { includeYear = false } = {}) {
  if (!iso) return "";
  const d = new Date(`${iso}T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, {
    weekday: "short",
    month: "short",
    day: "numeric",
    ...(includeYear ? { year: "numeric" } : {}),
    timeZone: "UTC",
  });
}

// "14:05" -> "2:05 PM". Pure string reformat for the same reason
// venueDateText is: there is no instant here, and a local-zone round-trip
// through a bare "HH:mm" would shift the hour.
export function venueTimeText(hhmm) {
  const m = /^(\d{1,2}):(\d{2})/.exec(String(hhmm || ""));
  if (!m) return hhmm || "";
  const h24 = Number(m[1]);
  if (h24 > 23 || Number(m[2]) > 59) return hhmm;
  const h = h24 % 12 || 12;
  return `${h}:${m[2]} ${h24 >= 12 ? "PM" : "AM"}`;
}

// The short zone label at instant `atMs` ("EDT", "MDT", "GMT+9"). Pass an IANA
// id for a specific zone, or omit it for the viewer's own. DST-aware because it
// resolves at a real instant, so a January game reads EST where a September one
// reads EDT. Returns "" rather than throwing on an invalid zone, so a bad row
// degrades to an unlabeled time instead of blanking the row.
//
// formatToParts, not a regex over the formatted string: the label's position
// and separator are locale-dependent, so slicing it off by hand breaks on any
// locale that doesn't put it last.
function zoneLabel(atMs, timeZone) {
  try {
    const parts = new Intl.DateTimeFormat("en-US", {
      ...(timeZone ? { timeZone } : {}),
      timeZoneName: "short",
    }).formatToParts(new Date(atMs));
    return parts.find((p) => p.type === "timeZoneName")?.value || "";
  } catch {
    return "";
  }
}

/**
 * What a game row renders as, in the viewer's zone.
 *
 * `venueDate` names the wall-clock date field because the envelopes disagree:
 * polyp-league and polyp-coach send `gameDate`, polyp-official-games sends
 * `agreedDate` for the same value.
 *
 * Returns `{ date, time }`. `time` is "" when there is no clock to show — a
 * date-only game — so callers can omit the separator rather than print a
 * stray "·".
 */
export function localWhen(game, { includeYear = false, venueDate = "gameDate" } = {}) {
  const g = game || {};
  const date = g[venueDate] || null;
  const time = g.gameTime || null;

  const tipOff = g.startsAtUtc ? Date.parse(g.startsAtUtc) : NaN;

  // No instant to convert: either the row predates startsAtUtc, or the route
  // doesn't publish it, or there is genuinely no clock. Render the venue's
  // wall clock as stored and label it with the VENUE's zone when we know it —
  // an honest "2:05 PM ET" beats a converted time we can't actually compute.
  if (!time || Number.isNaN(tipOff)) {
    const label =
      date && time && g.timeZone ? zoneLabel(Date.parse(`${date}T00:00:00Z`), g.timeZone) : "";
    const clock = time ? venueTimeText(time) : "";
    return {
      date: venueDateText(date, { includeYear }),
      time: clock && label ? `${clock} ${label}` : clock,
    };
  }

  const d = new Date(tipOff);
  const clock = d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
  const label = zoneLabel(tipOff);
  return {
    date: d.toLocaleDateString(undefined, {
      weekday: "short",
      month: "short",
      day: "numeric",
      ...(includeYear ? { year: "numeric" } : {}),
    }),
    time: label ? `${clock} ${label}` : clock,
  };
}

/** "Sat, Sep 13 · 2:05 PM EDT", or just the date when there is no clock. */
export function localWhenLine(game, opts) {
  const { date, time } = localWhen(game, opts);
  return time ? `${date} · ${time}` : date;
}
